import { useQuery, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { apiClient } from "./client.api";
import axios from "axios";
import { tenantService } from "@/services/tenant/tentant.service";
import isNil from "lodash.isnil";
import { toast } from "sonner";
import { CreateRoleDto, UpdateRoleDto } from "@exibra/shared";
import { useTranslation } from "react-i18next";

export interface Tenant {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateTenantDto {
    name: string;
    billing: {
        address: string;
        city: string;
        country: string;
        postalCode: string;
        email: string;
    };
}

export const tenantApiQueryKeys = {
    tenants: () => ["tenants"] as const,
    tenant: (id: number) => ["tenants", id] as const,
    tenantRoles: (id: number) => ["tenants", id, "roles"] as const,
} as const;

export const tenantApi = {
    /**
     * Create a new tenant
     */
    createTenant: async (tenantData: CreateTenantDto): Promise<Tenant> => {
        try {
            console.log("Creating tenant:", tenantData);
            const response = await apiClient.post("/tenants", tenantData);
            console.log("Create tenant response:", response.data);
            return response.data.tenant;
        } catch (error: unknown) {
            console.error("Error creating tenant:", error);
            if (axios.isAxiosError(error)) {
                console.error("Response data:", error.response?.data);
                console.error("Response status:", error.response?.status);
            }
            throw error;
        }
    },
};

/**
 * Hook to get the available tenants
 */
export const useTenants = () => {
    return useQuery({
        queryKey: tenantApiQueryKeys.tenants(),
        queryFn: () => tenantService.getAvailableTenants(),
        throwOnError: false,
    });
};

/**
 * Hook to get a specific tenant with suspense
 */
export const useSuspenseTenant = (id: number) => {
    return useSuspenseQuery({
        queryKey: tenantApiQueryKeys.tenant(id),
        queryFn: () => (Number.isSafeInteger(id) && id > 0 ? tenantService.getTenant(id) : null),
    });
};

/**
 * Hook to get a specific tenant
 */
export const useTenant = (id?: number) => {
    return useQuery({
        queryKey: tenantApiQueryKeys.tenant(id!),
        queryFn: () => tenantService.getTenant(id!),
        enabled: !isNil(id),
        throwOnError: false,
    });
};

/**
 * Create a new tenant
 */
export const useCreateTenant = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (tenant: CreateTenantDto) => tenantApi.createTenant(tenant),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: tenantApiQueryKeys.tenants() });
            toast.success(t("tenant.create.success"));
        },
        onError: () => {
            toast.error(t("tenant.create.error"));
        },
    });
};

/**
 * Hook to get tenant roles
 */
export const useTenantRoles = (id?: number) => {
    return useQuery({
        queryKey: tenantApiQueryKeys.tenantRoles(id!),
        queryFn: () => tenantService.getTenantRoles(id!),
        enabled: !isNil(id),
        throwOnError: false,
    });
};

/**
 * Hook to create a new role
 */
export const useCreateTenantRole = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: ({ tenantId, roleData }: { tenantId: number; roleData: CreateRoleDto }) =>
            tenantService.createTenantRole(tenantId, roleData),
        onSuccess: (_, { tenantId }) => {
            queryClient.invalidateQueries({ queryKey: tenantApiQueryKeys.tenantRoles(tenantId) });
            toast.success(t("users.roles.success.created"));
        },
        onError: () => {
            toast.error(t("users.roles.error.create"));
        },
    });
};

/**
 * Hook to update a role
 */
export const useUpdateTenantRole = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: ({
            tenantId,
            roleId,
            roleData,
        }: {
            tenantId: number;
            roleId: number;
            roleData: UpdateRoleDto;
        }) => tenantService.updateTenantRole(tenantId, roleId, roleData),
        onSuccess: (_, { tenantId }) => {
            queryClient.invalidateQueries({ queryKey: tenantApiQueryKeys.tenantRoles(tenantId) });
            toast.success(t("users.roles.success.updated"));
        },
        onError: () => {
            toast.error(t("users.roles.error.update"));
        },
    });
};

/**
 * Hook to delete a role
 */
export const useDeleteTenantRole = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: ({ tenantId, roleId }: { tenantId: number; roleId: number }) =>
            tenantService.deleteTenantRole(tenantId, roleId),
        onSuccess: (_, { tenantId }) => {
            queryClient.invalidateQueries({ queryKey: tenantApiQueryKeys.tenantRoles(tenantId) });
            toast.success(t("users.roles.delete.success"));
        },
        onError: () => {
            toast.error(t("users.roles.delete.error"));
        },
    });
};
